'use client';

// `palbe/react` — thin React hooks over the observable `pb.*` facades. Each
// value hook is a `useSyncExternalStore` (the React 18+ concurrent-safe
// primitive) binding: `subscribe` is the facade's Unsubscribe-returning
// listener, `getSnapshot` is the matching getter. NO new SDK behaviour lives
// here — the hooks re-render exactly when (and only when) their slice of state
// changes, mirroring the iOS `@Observable` granularity.
//
// `react` is an OPTIONAL peer dependency: importing `palbe` (the core entry)
// never pulls React; only `palbe/react` does.
//
// Client-only: the module starts with `'use client'` (Next.js App Router). SSR
// initial values come from the `getServerSnapshot` arms (null user, empty flag
// set) so a server render never touches the runtime singleton.

import { useCallback, useEffect, useRef, useState, useSyncExternalStore } from 'react';
import type { AuthUser, Unsubscribe } from '../auth-facade.js';
import type { FlagsView, FlagValue } from '../flags-facade.js';
import { onConfigured } from '../internal.js';
import type { Chat } from '../messaging/chat.js';
import type { ChatMember, ChatMessage } from '../messaging/types.js';
import { pb } from '../pb.js';
import type { RealtimeConnectionState, RealtimePayload } from '../realtime/facade.js';

/** The session slice `useSession` exposes. */
export interface SessionState {
  signedIn: boolean;
  user: AuthUser | null;
}

/** Frozen empty flag view — the stable server/unconfigured fallback for
 * `useFlags` (a fresh `{}` each call would loop `useSyncExternalStore`). */
const EMPTY_FLAGS: FlagsView = Object.freeze({});

/** The signed-out session snapshot — a single frozen instance so the
 * unconfigured/server snapshot is referentially stable across calls. */
const SIGNED_OUT: SessionState = Object.freeze({ signedIn: false, user: null });

/**
 * Wraps a facade-listener factory so hooks that mount BEFORE `__configure`
 * runs self-heal when configuration arrives (or changes on re-configure /
 * watch-mode regen).
 *
 * Behaviour:
 *  - If already configured at subscribe time, attaches the facade listener
 *    immediately AND registers an `onConfigured` watcher for future re-configures.
 *  - If NOT yet configured at subscribe time, only registers `onConfigured`;
 *    the facade listener is attached when configuration arrives.
 *  - On every configure event: detaches the old facade listener (if any),
 *    attaches a fresh one against the new runtime, then calls `onStoreChange`
 *    so React re-reads the snapshot from the new runtime.
 *  - The returned unsubscribe tears down both the onConfigured registration
 *    and the current facade listener.
 *
 * @param getFacadeListener - factory called when a runtime is available;
 *   must return an Unsubscribe; called with `onStoreChange` as its argument.
 */
function subscribeWithConfig(
  getFacadeListener: (onStoreChange: () => void) => Unsubscribe,
): (onStoreChange: () => void) => Unsubscribe {
  return (onStoreChange: () => void): Unsubscribe => {
    let currentFacadeUnsub: Unsubscribe | null = null;

    function attachFacade(): void {
      currentFacadeUnsub?.();
      try {
        currentFacadeUnsub = getFacadeListener(onStoreChange);
      } catch {
        // notConfigured — will be retried when onConfigured fires
        currentFacadeUnsub = null;
      }
    }

    // Register for future (re-)configure events.
    const offConfigured = onConfigured(() => {
      attachFacade();
      // Notify React to re-read the snapshot from the new runtime.
      onStoreChange();
    });

    // If already configured right now, attach immediately too.
    attachFacade();

    return () => {
      offConfigured();
      currentFacadeUnsub?.();
      currentFacadeUnsub = null;
    };
  };
}

/**
 * Current authenticated user, or `null` when signed out. Re-renders on BOTH
 * auth-state transitions (sign-in adopts a user, sign-out clears it) and
 * user-profile changes (e.g. an email-verified flip via `refreshUser`).
 *
 * `getSnapshot` is guarded: before the generated `palbe.gen.ts` runs
 * `__configure`, `pb.auth` throws `notConfigured` — a tree rendered that early
 * gets `null`, never a render crash. Once `__configure` runs the hook
 * self-heals: it re-subscribes to the new runtime's auth facade and notifies
 * React to re-read the snapshot.
 */
export function useUser(): AuthUser | null {
  return useSyncExternalStore(subscribeUserWithConfig, getUserSnapshot, getNullUser);
}

const subscribeUserWithConfig = subscribeWithConfig((onStoreChange) => {
  // Two sources, one composite unsubscribe:
  //   onAuthStateChange → sign-in (new user) and sign-out (null)
  //   onUserChange      → in-place profile edits (email-verified, …)
  // Both fire immediately on subscribe (iOS parity), which simply re-reads the
  // already-current snapshot — harmless.
  const offState = pb.auth.onAuthStateChange(onStoreChange);
  const offUser = pb.auth.onUserChange(onStoreChange);
  return () => {
    offState();
    offUser();
  };
});

function getUserSnapshot(): AuthUser | null {
  try {
    return pb.auth.currentUser;
  } catch {
    return null;
  }
}

function getNullUser(): AuthUser | null {
  return null;
}

/**
 * Session slice: `{ signedIn, user }`. Re-renders on auth-state transitions.
 *
 * `getSnapshot` MUST be referentially stable when nothing changed — returning a
 * fresh object every call would put `useSyncExternalStore` into an infinite
 * render loop. A per-component ref caches the last value and returns the SAME
 * object until `signedIn` or `user` actually changes.
 *
 * Self-heals when `__configure` runs after mount (same as `useUser`).
 */
export function useSession(): SessionState {
  const lastRef = useRef<SessionState>(SIGNED_OUT);
  const getSnapshot = useCallback((): SessionState => {
    const next = readSession();
    const prev = lastRef.current;
    if (prev.signedIn === next.signedIn && prev.user === next.user) {
      return prev; // unchanged — keep the stable reference
    }
    lastRef.current = next;
    return next;
  }, []);
  return useSyncExternalStore(subscribeSessionWithConfig, getSnapshot, getSignedOut);
}

const subscribeSessionWithConfig = subscribeWithConfig((onStoreChange) =>
  pb.auth.onAuthStateChange(onStoreChange),
);

function readSession(): SessionState {
  try {
    const user = pb.auth.currentUser;
    const signedIn = pb.auth.isSignedIn;
    if (!signedIn && user === null) return SIGNED_OUT;
    return { signedIn, user };
  } catch {
    return SIGNED_OUT;
  }
}

function getSignedOut(): SessionState {
  return SIGNED_OUT;
}

/**
 * Subscribe to ONE flag. Re-renders only when THIS key's value changes
 * (`pb.flags.subscribeKey` is the per-key source — structural compare for
 * objects), returning `fallback` until the key has a value.
 *
 * The value is read at the dynamic flag boundary where the concrete type `T`
 * is caller-asserted (the cached value is whatever the server sent); the
 * contained `as T` is the documented escape hatch — flags are untyped on the
 * wire, and the caller owns the `fallback`'s type.
 *
 * NOTE: passing an inline-object `fallback` for an absent key defeats snapshot
 * caching (a new object reference each render re-enters the last-value ref).
 * Memoize the fallback (e.g. a module-level const or `useMemo`) if it matters.
 *
 * Self-heals when `__configure` runs after mount (same as `useUser`).
 */
export function useFlag<T extends FlagValue>(key: string, fallback: T): T {
  // Subscribe identity must change when `key` changes so useSyncExternalStore
  // re-subscribes to the new key. subscribeWithConfig wraps the per-key
  // factory so the hook also self-heals on (re-)configure.
  // biome-ignore lint/correctness/useExhaustiveDependencies: `key` is a runtime param, not an outer-scope value — dependency IS required
  const subscribe = useCallback(
    subscribeWithConfig((onStoreChange) => pb.flags.subscribeKey(key, onStoreChange)),
    [key],
  );

  // Cache the last value so getSnapshot is referentially stable (objects come
  // back as the frozen pooled reference — stable identity until a real change).
  const lastRef = useRef<T>(fallback);
  const getSnapshot = useCallback((): T => {
    let next: T;
    try {
      const value = pb.flags.get(key);
      next = value === undefined ? fallback : (value as T);
    } catch {
      next = fallback;
    }
    if (!Object.is(next, lastRef.current)) {
      lastRef.current = next;
    }
    return lastRef.current;
  }, [key, fallback]);

  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}

/**
 * Subscribe to the whole flag set. Re-renders on any change. `pb.flags.all()`
 * returns a frozen, identity-stable view (a NEW frozen object only when the set
 * actually changes), so it is `useSyncExternalStore`-safe as-is.
 *
 * Self-heals when `__configure` runs after mount (same as `useUser`).
 */
export function useFlags(): FlagsView {
  return useSyncExternalStore(subscribeFlagsWithConfig, getFlagsSnapshot, getEmptyFlags);
}

const subscribeFlagsWithConfig = subscribeWithConfig((onStoreChange) =>
  pb.flags.onChange(onStoreChange),
);

function getFlagsSnapshot(): FlagsView {
  try {
    return pb.flags.all();
  } catch {
    return EMPTY_FLAGS;
  }
}

function getEmptyFlags(): FlagsView {
  return EMPTY_FLAGS;
}

/** The reported channel status: the shared-socket connection state, plus
 * `'unavailable'` when `pb.realtime.channel()` is not usable in this
 * environment (no WebSocket / SSR). */
export type ChannelStatus = RealtimeConnectionState | 'unavailable';

/**
 * Subscribe a `handler` to one realtime `event` on `channel(name)` for the
 * lifetime of the component. Returns the live connection `{ status }`.
 *
 * The handler is held in a ref and refreshed every render, so passing a fresh
 * closure each render (the common case — callers needn't `useCallback`) does
 * NOT tear down and re-create the subscription. The subscription itself is
 * keyed on `[name, event]`: only a name/event change re-subscribes.
 *
 * StrictMode-safe: the facade refcounts joins, so React's mount→cleanup→
 * remount (double-invoke in dev) nets to exactly one live subscription via the
 * effect's `cancel()` cleanup — NO once-guard (a once-guard would leave the
 * subscription dead after the first cleanup; that was the P4 live-smoke bug).
 *
 * SSR / no-WebSocket: `pb.realtime.channel()` throws; the effect catches it and
 * reports `status: 'unavailable'` instead of crashing. (Effects don't run on
 * the server, so the initial server status is the idle default.)
 */
export function useChannel(
  name: string,
  event: string,
  handler: (payload: RealtimePayload) => void,
): { status: ChannelStatus } {
  const handlerRef = useRef(handler);
  // Refresh the handler every render WITHOUT re-subscribing — the effect below
  // calls `handlerRef.current`, so the latest closure always runs.
  useEffect(() => {
    handlerRef.current = handler;
  });

  const [status, setStatus] = useState<ChannelStatus>('idle');

  // A configure-epoch counter: bumped by onConfigured so the channel effect
  // re-runs when a runtime arrives (handles the pre-config mount case and
  // watch-mode runtime swaps). Does not re-subscribe on every render.
  const [configEpoch, setConfigEpoch] = useState(0);
  useEffect(() => {
    return onConfigured(() => setConfigEpoch((n) => n + 1));
  }, []);

  // biome-ignore lint/correctness/useExhaustiveDependencies: `configEpoch` is intentionally a re-run trigger for configure-arrive / watch-mode
  useEffect(() => {
    let channel: ReturnType<typeof pb.realtime.channel>;
    try {
      channel = pb.realtime.channel(name);
    } catch {
      // notConfigured or no-WebSocket / server environment — degrade gracefully.
      setStatus('unavailable');
      return;
    }
    const sub = channel.on(event, (payload) => handlerRef.current(payload));
    // Seed from the current connection state, then track transitions.
    setStatus(pb.realtime.status.state);
    const offStatus = pb.realtime.status.onChange((snapshot) => setStatus(snapshot.state));
    return () => {
      offStatus();
      sub.cancel();
    };
  }, [name, event, configEpoch]);

  return { status };
}

// ─── Messaging hooks (Web-MLS Phase 2) ───────────────────────────────────────

const EMPTY_CHATS: readonly Chat[] = Object.freeze([]);
const EMPTY_MESSAGES: readonly ChatMessage[] = Object.freeze([]);
const EMPTY_MEMBERS: readonly ChatMember[] = Object.freeze([]);
const EMPTY_TYPING: readonly ChatMember[] = Object.freeze([]);

/**
 * The observable chat list (DMs + groups, active only). Re-renders when a chat
 * is added (you start one / a peer DMs you and the Welcome drains), removed, or
 * the list hydrates from the durable catalog on launch.
 *
 * Self-heals when `__configure` runs after mount (same as `useUser`).
 */
export function useChats(): readonly Chat[] {
  const lastRef = useRef<readonly Chat[]>(EMPTY_CHATS);
  const getSnapshot = useCallback((): readonly Chat[] => {
    try {
      const next = pb.messaging.chats;
      // Identity-stable: only swap the cached array when the set changed.
      if (next.length !== lastRef.current.length || next.some((c, i) => c !== lastRef.current[i])) {
        lastRef.current = next.slice();
      }
      return lastRef.current;
    } catch {
      return EMPTY_CHATS;
    }
  }, []);
  return useSyncExternalStore(subscribeChatsWithConfig, getSnapshot, getEmptyChats);
}

const subscribeChatsWithConfig = subscribeWithConfig((onStoreChange) =>
  pb.messaging.onChatsChange(onStoreChange),
);

function getEmptyChats(): readonly Chat[] {
  return EMPTY_CHATS;
}

/**
 * Bind to one `Chat`'s changes. Re-renders whenever the chat's observable state
 * changes (a new message, members refresh, typing, materialize draft→active).
 * Returns the SAME `chat` instance for ergonomic access to `chat.messages`,
 * `chat.send(...)`, etc.
 */
export function useChat(chat: Chat): Chat {
  const subscribe = useCallback(
    (onStoreChange: () => void) => chat.onChange(onStoreChange),
    [chat],
  );
  // The chat instance is pointer-stable; a per-render counter forces re-render
  // on each onChange. Snapshot returns the instance itself (stable identity).
  const getSnapshot = useCallback(() => chat, [chat]);
  // Tick on every change so consumers reading chat.messages re-render.
  const [, force] = useState(0);
  useEffect(() => chat.onChange(() => force((n) => n + 1)), [chat]);
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}

/**
 * The ordered message transcript for a chat (newest last). Re-renders on a new
 * message / history page / own-send echo. A cached snapshot keeps the array
 * reference stable until the list actually changes.
 */
export function useMessages(chat: Chat): readonly ChatMessage[] {
  const lastRef = useRef<readonly ChatMessage[]>(EMPTY_MESSAGES);
  const subscribe = useCallback(
    (onStoreChange: () => void) => chat.onChange(onStoreChange),
    [chat],
  );
  const getSnapshot = useCallback((): readonly ChatMessage[] => {
    const next = chat.messages;
    if (next.length !== lastRef.current.length || next.some((m, i) => m !== lastRef.current[i])) {
      lastRef.current = next.slice();
    }
    return lastRef.current;
  }, [chat]);
  return useSyncExternalStore(subscribe, getSnapshot, () => EMPTY_MESSAGES);
}

/** The members of a chat (users). Re-renders when the roster changes. */
export function useChatMembers(chat: Chat): readonly ChatMember[] {
  const lastRef = useRef<readonly ChatMember[]>(EMPTY_MEMBERS);
  const subscribe = useCallback(
    (onStoreChange: () => void) => chat.onChange(onStoreChange),
    [chat],
  );
  const getSnapshot = useCallback((): readonly ChatMember[] => {
    const next = chat.members;
    if (next.length !== lastRef.current.length || next.some((m, i) => m !== lastRef.current[i])) {
      lastRef.current = next.slice();
    }
    return lastRef.current;
  }, [chat]);
  return useSyncExternalStore(subscribe, getSnapshot, () => EMPTY_MEMBERS);
}

/** The users currently typing in a chat. Re-renders on typing start/stop. */
export function useTyping(chat: Chat): readonly ChatMember[] {
  const lastRef = useRef<readonly ChatMember[]>(EMPTY_TYPING);
  const subscribe = useCallback(
    (onStoreChange: () => void) => chat.onChange(onStoreChange),
    [chat],
  );
  const getSnapshot = useCallback((): readonly ChatMember[] => {
    const next = chat.typing;
    if (next.length !== lastRef.current.length || next.some((m, i) => m !== lastRef.current[i])) {
      lastRef.current = next.slice();
    }
    return lastRef.current;
  }, [chat]);
  return useSyncExternalStore(subscribe, getSnapshot, () => EMPTY_TYPING);
}
